import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Trophy, Medal, Star, TrendingUp, Award, Plus } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/api";

type Milestone = {
  id: string;
  athleteId: string;
  title: string;
  description?: string | null;
  date: string;
  type?: string | null;
};

type AchievementShowcaseProps = {
  athleteId: string;
};

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  game: Trophy,
  tournament: Medal,
  personal: Star,
  academic: TrendingUp,
  award: Award,
};

const CATEGORY_LABELS: Record<string, string> = {
  game: "Game Performance",
  tournament: "Tournament",
  award: "Award",
  academic: "Academic",
  personal: "Personal Best",
};

async function throwIfNotOk(res: Response) {
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error((data as any).message || `Request failed (${res.status})`);
  }
  return res;
}

const AchievementShowcase: React.FC<AchievementShowcaseProps> = ({ athleteId }) => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    date: new Date().toISOString().slice(0, 10),
    category: "game",
  });

  const { data: milestones = [], isLoading } = useQuery<Milestone[]>({
    queryKey: ["/api/athletes", athleteId, "milestones"],
    enabled: !!athleteId,
  });

  const achievements = milestones.filter((m) => m.type === "achievement");

  const addMutation = useMutation({
    mutationFn: () =>
      apiRequest(`/athletes/${athleteId}/milestones`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: form.title,
          description: form.description,
          date: form.date,
          type: "achievement",
          category: form.category,
        }),
      }).then(throwIfNotOk),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/athletes", athleteId, "milestones"] });
      setDialogOpen(false);
      setForm({ title: "", description: "", date: new Date().toISOString().slice(0, 10), category: "game" });
      toast({ title: "Achievement saved" });
    },
    onError: (err) =>
      toast({
        title: "Failed to save achievement",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      }),
  });

  const handleSave = () => {
    if (!form.title.trim()) return;
    addMutation.mutate();
  };

  return (
    <Card className="w-full border border-[#A67C52] bg-[#111827] text-white">
      <CardHeader className="pb-3 border-b border-[#E0A458]/20">
        <div className="flex items-center justify-between">
          <CardTitle className="font-montserrat font-bold text-base text-white flex items-center gap-2">
            <Trophy className="h-4 w-4 text-[#E0A458]" />
            Achievement Showcase
          </CardTitle>
          <Button
            size="sm"
            onClick={() => setDialogOpen(true)}
            className="bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold h-7 px-2 text-xs"
          >
            <Plus className="h-3 w-3 mr-1" /> Add
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pt-4 px-4 pb-4">
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <div key={i} className="h-16 rounded-lg bg-[#1B263B] animate-pulse" />
            ))}
          </div>
        ) : achievements.length === 0 ? (
          <div className="flex flex-col items-center py-8 text-center">
            <Award className="h-8 w-8 text-[#E0A458]/40 mb-2" />
            <p className="text-slate-300 text-sm font-semibold">No achievements yet</p>
            <p className="text-xs text-slate-400 mt-1">Add your first win, award, or milestone.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {achievements.map((achievement) => {
              const Icon = CATEGORY_ICONS[achievement.type || "game"] ?? Trophy;
              return (
                <div
                  key={achievement.id}
                  className="flex items-start gap-3 rounded-lg border border-[#415A77]/40 bg-[#0D1B2A]/60 p-3"
                >
                  <div className="h-8 w-8 rounded-full bg-[#E0A458]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon className="h-4 w-4 text-[#E0A458]" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-white leading-snug">{achievement.title}</p>
                    {achievement.description && (
                      <p className="text-xs text-slate-400 mt-0.5 leading-snug">{achievement.description}</p>
                    )}
                    <p className="text-xs text-slate-500 mt-1">
                      {new Date(`${achievement.date}T00:00:00Z`).toLocaleDateString("en-US", {
                        month: "short", day: "numeric", year: "numeric", timeZone: "UTC",
                      })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0D1B2A] border border-[#E0A458]/30 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-montserrat font-bold">Add Achievement</DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-slate-400 font-semibold uppercase tracking-wide">Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Regional MVP, Personal Best..."
                className="mt-1 bg-[#0B1220] border-[#415A77]/60 text-white placeholder:text-slate-500"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400 font-semibold uppercase tracking-wide">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger className="mt-1 bg-[#0B1220] border-[#415A77]/60 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0D1B2A] border-[#415A77]/60 text-white">
                  {Object.entries(CATEGORY_LABELS).map(([val, label]) => (
                    <SelectItem key={val} value={val}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400 font-semibold uppercase tracking-wide">Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                className="mt-1 bg-[#0B1220] border-[#415A77]/60 text-white"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400 font-semibold uppercase tracking-wide">Notes (optional)</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="What made this moment special?"
                rows={2}
                className="mt-1 bg-[#0B1220] border-[#415A77]/60 text-white placeholder:text-slate-500 resize-none"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-400 hover:text-white">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!form.title.trim() || addMutation.isPending}
              className="bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold"
            >
              {addMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AchievementShowcase;
